import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDoctorById, getDoctorAnalytics, getDoctorAvatarSrc, type DoctorAnalyticsDTO, type DoctorDTO } from '@/services/doctors';
import { useAuth } from '@/hooks/useAuth';
import { SEOBox } from '@/components/seo/SEOBox';
import { SchemaMarkup } from '@/components/seo/SchemaMarkup';
import { Loader2, ArrowLeft, Stethoscope, Flag, Star, Users, MapPin, Phone } from 'lucide-react';
import { Button, Card, Badge } from '@/components/common';
import { ReportModal } from '@/components/common/ReportModal';
import { cn } from '@/lib/utils';
import { formatDoctorCrmDisplay, hasPublicDoctorCrm } from '@/lib/doctorCrm';

export default function PublicDoctorProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doctor, setDoctor] = useState<DoctorDTO | null>(null);
  const [analytics, setAnalytics] = useState<DoctorAnalyticsDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [docRes, analyticsRes] = await Promise.all([
          getDoctorById(id),
          getDoctorAnalytics(id)
        ]);
        
        if (docRes.success && docRes.data) {
          setDoctor(docRes.data);
        }
        
        if (analyticsRes.success && analyticsRes.data) {
          setAnalytics(analyticsRes.data);
        }
      } catch (err) {
        console.error('Failed to load doctor profile data', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-neutral-950 p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Médico não encontrado</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">O perfil que você tentou acessar não existe ou está indisponível.</p>
        <Button onClick={() => navigate('/')}>Voltar para a Home</Button>
      </div>
    );
  }

  const doctorName = doctor.user?.name || 'Médico(a) App Saúde';
  const pageTitle = `${doctorName} - ${doctor.specialty} | App Saúde`;
  const pageDesc = doctor.biography || `Agende sua consulta com ${doctorName}, especialista em ${doctor.specialty}.`;
  const ratingLabel =
    analytics?.satisfactionRating !== null && analytics?.satisfactionRating !== undefined
      ? analytics.satisfactionRating.toFixed(1)
      : 'Sem avaliações ainda';
  const ratingsCount = analytics?.ratingsCount || 0;
  const patientsCount = analytics?.patientsCount || 0;
  const showCrm = hasPublicDoctorCrm(doctor.crm, doctor.crmState);

  const handleScheduleClick = () => {
    if (!user) {
      navigate('/login', { state: { from: `/medico/${doctor.id}` } });
      return;
    }
    navigate(`/agendamento/${doctor.id}`);
  };

  return (
    <>
      <SEOBox 
        title={pageTitle}
        description={pageDesc}
        ogType="profile"
      />
      <SchemaMarkup 
        type="Physician"
        data={{
          name: doctorName,
          jobTitle: doctor.specialty,
          medicalSpecialty: [doctor.specialty]
        }}
      />
      
      <main className="min-h-screen bg-gray-50 dark:bg-neutral-950 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Button variant="tertiary" onClick={() => navigate(-1)} className="mb-6 h-9 px-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>

          <Card className="p-8">
            <div className="flex flex-col md:flex-row gap-8 items-start md:items-center">
              {getDoctorAvatarSrc(doctor) ? (
                <img
                  src={getDoctorAvatarSrc(doctor)!}
                  alt={doctorName}
                  className="w-24 h-24 rounded-full object-cover flex-shrink-0 border border-teal-100 dark:border-teal-900"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
                  <Stethoscope className="w-10 h-10 text-teal-600 dark:text-teal-400" />
                </div>
              )}
              
              <div className="flex-1 w-full">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {doctorName}
                  </h1>
                  <Badge 
                    variant={doctor.isApproved ? "success" : "warning"} 
                    size="md"
                  >
                    {doctor.isApproved ? "Perfil Verificado" : "Aprovação Pendente"}
                  </Badge>
                </div>
                
                <p className="text-lg text-teal-600 dark:text-teal-400 font-medium mb-4">
                  {doctor.specialty}
                </p>

                {/* Quick Stats */}
                <div className="flex flex-wrap gap-6 mb-6 pb-6 border-b border-gray-100 dark:border-neutral-800">
                  <div className="flex items-center gap-2">
                    <Star className={cn("w-5 h-5", ratingsCount > 0 ? "text-amber-400 fill-amber-400" : "text-gray-300")} />
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {ratingLabel}
                      </p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">
                        {ratingsCount === 1 ? '1 avaliação' : `${ratingsCount} avaliações`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-teal-500" />
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {patientsCount}
                      </p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">
                        {patientsCount === 1 ? 'Paciente atendido' : 'Pacientes atendidos'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400 mb-8">
                  {showCrm && (
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-gray-300">CRM:</span>
                      {formatDoctorCrmDisplay(doctor.crm, doctor.crmState)}
                    </div>
                  )}
                  {doctor.consultationPrice && (
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-gray-300">Consulta:</span>
                      R$ {doctor.consultationPrice.toFixed(2).replace('.', ',')}
                    </div>
                  )}
                  {doctor.clinicAddress && (
                    <div className="flex items-start gap-2 col-span-1 sm:col-span-2">
                      <MapPin className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                      <span>{doctor.clinicAddress}</span>
                    </div>
                  )}
                  {doctor.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                      <span>{doctor.phone}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Sobre o especialista</h2>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-justify">
                    {doctor.biography || "Este profissional ainda não adicionou uma biografia ao seu perfil, mas é um especialista qualificado e validado pela plataforma App Saúde."}
                  </p>
                </div>

                {/* Documents / Verification Section */}
                {doctor.documents && doctor.documents.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-gray-100 dark:border-neutral-800">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Verificações e Certificações</h2>
                    <div className="flex flex-wrap gap-2">
                      {doctor.documents
                        .filter(doc => doc.status === 'APPROVED')
                        .map(doc => (
                          <Badge key={doc.id} variant="info" className="bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-gray-300">
                            {doc.documentType === 'CRM' ? 'CRM Validado' : 
                             doc.documentType === 'SPECIALTY_CERTIFICATE' ? 'Título de Especialista' : 
                             doc.documentType === 'INSCRIPTION_PROOF' ? 'Inscrição Regular' : 
                             doc.documentType === 'CONFIRMATION_VIDEO' ? 'Vídeo de confirmação' :
                             doc.documentType || 'Documento Verificado'}
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-neutral-800 flex justify-between items-center">
              {user && user.id !== doctor.userId && (
                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-600 transition-colors"
                >
                  <Flag className="w-4 h-4" />
                  Denunciar este profissional
                </button>
              )}
              <Button size="lg" className="bg-teal-600 text-white hover:bg-teal-700" onClick={handleScheduleClick}>
                Agendar Consulta
              </Button>
            </div>
          </Card>
        </div>

        {showReportModal && user && (
          <ReportModal
            reportedId={doctor.userId}
            reportedName={doctorName}
            onClose={() => setShowReportModal(false)}
          />
        )}
      </main>
    </>
  );
}
